/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

const baseStyle = {
  container: {
    position: 'relative',
    overflow: 'hidden',
    height: 'calc(100vh - 65px)',
    color: 'white',
    sx: {
      // Style .background-image-container from content asset
      '& .background-image-container': {
        'position': 'absolute',
        'top': 0,
        'left': 0,
        'right': 0,
        'bottom': 0,
        'zIndex': 1,
        'overflow': 'hidden',
        '& img': {
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.7)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.30) 70.73%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%)',
          zIndex: 2,
        },
      },
      // Content container styling
      '& .content-container': {
        'position': 'relative',
        'zIndex': 10,
        'display': 'flex',
        'flexDirection': 'column',
        'gap': {base: 4.5, lg: 5},
        // h1 (title) styles - scoped to content-container
        '& h1': {
          color: 'white',
          fontSize: {base: '32px', lg: '6xl'},
          fontWeight: 'normal',
          lineHeight: 'shorter',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          mb: 0,
        },
        // h2 (subtitle) styles - scoped to content-container
        '& h2': {
          color: 'white',
          fontSize: {base: 'xl', lg: '40px'},
          fontWeight: 'normal',
          lineHeight: 'shorter',
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          mb: 0,
        },
        // description wrapper styles
        '& .description': {
          'color': 'white',
          'fontSize': {base: 'md', lg: 'lg'},
          'lineHeight': 'base',
          // p (description) styles - scoped to description
          '& p': {
            color: 'white',
            fontSize: {base: 'md', lg: 'lg'},
            lineHeight: 1.6,
            mb: 0,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      // Hide button-container from content asset since buttons are React components
      '& .button-container': {
        display: 'flex',
        position: 'relative',
        zIndex: 10,
        flexBasis: {base: '100%', lg: 'auto'},
        alignItems: 'flex-start',
        flexDirection: {base: 'column', sm: 'row'},
        gap: 4,
        paddingTop: {base: '6px', lg: '12px'},
        sx: {
          '& .button': {
            transition: 'all 0.2s ease-in-out',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  },
  contentWrapper: {
    align: {base: 'flex-end', lg: 'center'},
    height: '100%',
    padding: {base: 5, lg: 20},
    pb: {base: 10, lg: 20},
    zIndex: 2,
    gap: 0,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: {base: 'normal', lg: 'flex-start'},
    gap: {base: 4.5, lg: 5},
    maxW: {base: '100%', md: '540px'},
    width: {base: '100%', lg: 'auto'},
  },
  buttonContainer: {
    position: 'relative',
    zIndex: 10,
    flexBasis: {base: '100%', lg: 'auto'},
    align: 'flex-start',
    direction: {base: 'column', sm: 'row'},
    spacing: 4,
    sx: {
      '& button': {
        transition: 'all 0.2s ease-in-out',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  backButton: {
    flexBasis: {base: '100%', lg: 'auto'},
    width: {base: '100%', lg: 'auto'},
    minW: 'auto',
    whiteSpace: 'nowrap',
    bg: 'transparent',
    borderColor: 'white',
    color: 'white',
    fontSize: 'sm',
    lineHeight: '1.2',
    fontWeight: 'semibold',
    minHeight: '13.5',
    px: {base: 3.5, lg: 12},
    py: {base: 3.5, lg: 4},
    variant: 'outline',
    _active: {
      bg: 'whiteAlpha.300',
    },
    _hover: {
      bg: 'whiteAlpha.200',
      borderColor: 'white',
      color: 'white',
    },
  },
  exploreButton: {
    flexBasis: {base: '100%', lg: 'auto'},
    width: {base: '100%', lg: 'auto'},
    minW: 'auto',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    bg: 'white',
    color: 'brandColors.greyscale',
    fontSize: 'sm',
    lineHeight: '1.2',
    fontWeight: 'semibold',
    minHeight: '13.5',
    px: {base: 3.5, lg: 12},
    py: {base: 3.5, lg: 4},
    _active: {
      transform: 'translateY(0)',
    },
    _hover: {
      bg: 'gray.100',
      transform: 'translateY(-1px)',
    },
  },
  // Markup wrapper for content asset - minimal styling needed since styles are in container sx
  markup: {},
};

const variants = {
  default: {
    container: {
      bg: 'brandColors.greyscale',
    },
  },
};

const defaultProps = {
  size: 'md',
  variant: 'default',
};

const PageNotFound = {
  parts: ['container', 'contentWrapper', 'textContainer', 'buttonContainer', 'backButton', 'exploreButton', 'markup'],
  baseStyle,
  variants,
  defaultProps,
};

export default PageNotFound;
